import { useState, useEffect } from 'react';

import { getTimestamp } from './helpers';
import { type ClinicalTrial, getClinicalTrials } from '../../_api/ClinicalTrialsApi';
import { type SnomedConcept, getSnomedConceptItem } from '../../_api/SnomedCTApi';
import { useSearchParams } from '../../_helpers/useSearchParams';

const FALLBACK_ERROR_MESSAGE = 'Feil: Kunne ikke laste kliniske studier';
const LOCAL_STORAGE_KEY = 'kliniskeStudier';
const ONE_HOUR = 60 * 60;

export interface ClinicalTrialsStorage {
  timestamp: number;
  kliniskeStudier: ClinicalTrial[];
}

const useClinicalTrails = (
  snowstormApiUrl: string,
  snomedCTBranch: string,
  clinicalTrialsApiUrl: string,
  loadingError?: string
): { kliniskeStudier: ClinicalTrial[]; concept: SnomedConcept | null; loading: boolean; error: string | null } => {
  const [kliniskeStudier, setKliniskeStudier] = useState<ClinicalTrial[]>([]);
  const [concept, setConcept] = useState<SnomedConcept | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const diagnose = searchParams?.get('diagnose');

  useEffect(() => {
    let cancelled = false;

    const fetchData = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        let fetchedConcept: SnomedConcept | null = null;
        if (diagnose) {
          fetchedConcept = await getSnomedConceptItem(diagnose, snowstormApiUrl, snomedCTBranch);
          if (!cancelled) {
            setConcept(fetchedConcept);
          }
        }

        // Hent kliniske studier fra local storage, og fra API hvis de er utdaterte eller ikke finnes
        let trials: ClinicalTrial[] = [];
        let useNetwork = true;

        try {
          const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (storedData) {
            const json = JSON.parse(storedData) as ClinicalTrialsStorage;
            if (getTimestamp() - json.timestamp <= ONE_HOUR && json.kliniskeStudier.length > 0) {
              trials = json.kliniskeStudier;
              useNetwork = false;
              localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ timestamp: getTimestamp(), kliniskeStudier: trials }));
            }
          }
        } catch {
          // Henter fra nettverket i tilfelle feil
        }

        if (useNetwork) {
          const fetchedTrials = await getClinicalTrials(clinicalTrialsApiUrl);
          if (fetchedTrials) {
            trials = fetchedTrials;
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ timestamp: getTimestamp(), kliniskeStudier: trials }));
          } else {
            throw new Error(loadingError ?? FALLBACK_ERROR_MESSAGE);
          }
        }

        if (!cancelled) {
          setKliniskeStudier(trials);
        }
      } catch {
        if (!cancelled) {
          setError(loadingError ?? FALLBACK_ERROR_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return (): void => {
      cancelled = true;
    };
  }, [clinicalTrialsApiUrl, loadingError, snowstormApiUrl, snomedCTBranch, diagnose]);

  return { kliniskeStudier, concept, loading, error };
};

export default useClinicalTrails;
